import { Route } from 'vue-router';
import { Optional, TypedDict } from 'frontend/types';
import { LessonState } from 'frontend/types/lessons/lesson';
import { TranspilerWarning } from './warnings';
import {
  LegacyJavascriptInteractiveTypeName,
  JSONConfigInteractiveTypeName,
  Interactive,
  InteractiveTypeName,
  JSONConfigInteractive,
  JSON_CONFIG_TYPES,
  ELM_TYPES,
  ALL_INTERACTIVE_TYPES,
  LegacyJavascriptInteractive,
  ElmInteractiveTypeName,
  DIAGRAMMAR_TYPE_NAME,
  SIMSON_TYPE_NAME,
  ELM_CONFIG_TYPE_NAME,
  LEGACY_JAVASCRIPT_TYPES,
  GENERIC_ELM_ASSET_TYPE_NAME,
} from 'frontend/types/interactiveTypes';
import { InteractivesManifest } from 'visualizations/shared/interactivesManager/InteractivesManifest';

export type LessonAssetTypeName =
  | InteractiveTypeName
  | SampleDiagramTypeName
  | ImageTypeName
  | VideoTypeName;

export type LessonAsset =
  | ImageAsset
  | GenericElmAsset
  | DiagrammarAsset
  | SimsonAsset
  | JSONConfigAsset
  | LegacyJavascriptAsset
  | ElmConfigAsset
  | VideoAsset;

export type InteractiveAsset =
  | DiagrammarAsset
  | SimsonAsset
  | GenericElmAsset
  | JSONConfigAsset
  | LegacyJavascriptAsset
  | ElmConfigAsset;

export type ImageTypeName = 'image';

export type VideoTypeName = 'video';

export type VideoBehavior = 'gif' | 'video';

export type SampleDiagramTypeName =
  | 'area-under-plot'
  | 'parametric-functions-ellipses'
  | 'slider-input-squircle'
  | 'histogram'
  | 'local-tangent'
  | 'canon-plot';

/*
 * You may wonder, what's the difference between a BaseAsset and a BaseInteractive
 *
 * BaseAsset is the same, but includes the addition of:
 *   - Static items like Video and Image
 *   - Example versions of things (like the sample diagrams)
 *
 * There are some type level assumptions that work because
 * LessonAssetTypeName is a super set of InteractiveTypeName,
 * and if that assumption is violated, there will be cascading type
 * errors that fall out of that problem.
 *
 */
interface BaseAsset {
  alias: string;
  type: LessonAssetTypeName;
}

export interface ImageAsset extends BaseAsset {
  type: ImageTypeName;
  url: string;
  width: string;
  altText: string;
  figmaUrl?: string;
}

export interface VideoAsset extends BaseAsset {
  type: VideoTypeName;
  url: string;
  poster?: string;
  behavior: VideoBehavior;
}

export interface FigmaImage {
  url: string;
  slug: string;
  width: number;
  height: number;
  figmaUrl: string;
}

export interface ElmCompiledAsset extends BaseAsset {
  type: ElmInteractiveTypeName;
  compilationStatus?: CompilationStatus;
  elm: string;
  userLibs?: string[];
  isSolvable?: boolean;
}

export type CompilationStatus = 'compiled' | 'failed_to_compile';

// tslint:disable-next-line:no-empty-interface
export interface JSONConfigAsset extends JSONConfigInteractive {}

export interface DiagrammarAsset extends ElmCompiledAsset {
  type: typeof DIAGRAMMAR_TYPE_NAME;
}

export interface SimsonAsset extends ElmCompiledAsset {
  type: typeof SIMSON_TYPE_NAME;
}

export interface GenericElmAsset extends ElmCompiledAsset {
  type: typeof GENERIC_ELM_ASSET_TYPE_NAME;
}

// tslint:disable-next-line:no-empty-interface
export interface LegacyJavascriptAsset extends LegacyJavascriptInteractive {}

export interface ElmConfigAsset extends ElmCompiledAsset {
  type: typeof ELM_CONFIG_TYPE_NAME;
}

export interface LessonAssetVersion {
  lessonVersion: string;
  state: string;
  createdByName: string;
  ownedByName: string;
  createdTs: string;
  lastModifiedTs: string;
  internalChangeReason: string;
  asset: LessonAsset;
}

export type LessonAssetAliasMap = Record<string, LessonAssetVersion[]>;

export interface InteractiveSchema {
  [key: string]: any;
}
export type InteractiveSchemaDict = {
  [key in JSONConfigInteractiveTypeName | LegacyJavascriptInteractiveTypeName]: InteractiveSchema;
};

export function isDiagrammarAsset(asset: LessonAsset): asset is DiagrammarAsset {
  return asset.type === DIAGRAMMAR_TYPE_NAME;
}

export function isSolvableDiagrammarAsset(asset: LessonAsset): boolean {
  return asset.type === DIAGRAMMAR_TYPE_NAME && Boolean(asset.isSolvable);
}

export function isSimsonAsset(asset: LessonAsset): asset is SimsonAsset {
  return asset.type === SIMSON_TYPE_NAME;
}

export function isGenericElmAsset(asset: LessonAsset): asset is GenericElmAsset {
  return asset.type === GENERIC_ELM_ASSET_TYPE_NAME;
}

export function isElmConfigAsset(asset: LessonAsset): asset is ElmConfigAsset {
  return asset.type === ELM_CONFIG_TYPE_NAME;
}

// In theory, diagrammar is one of these as well
// but the editor (for now) doesn't treat it as such
export function isElmCompiledAsset(asset: LessonAsset): asset is ElmCompiledAsset {
  return asset.type in ELM_TYPES;
}

export function isEmbeddedInteractive(asset: LessonAsset): boolean {
  return isSimsonAsset(asset);
}

export function isLegacyJavascriptAsset(asset: LessonAsset): asset is LegacyJavascriptAsset {
  return asset.type in LEGACY_JAVASCRIPT_TYPES;
}

export function isJSONConfigAsset(
  asset: LessonAsset,
): asset is JSONConfigAsset | LegacyJavascriptInteractive {
  return asset.type in JSON_CONFIG_TYPES;
}

export function isInteractiveAsset(asset: LessonAsset): asset is InteractiveAsset {
  return ALL_INTERACTIVE_TYPES.hasOwnProperty(asset.type);
}
export function defaultInteractiveSelection(alias: string): Interactive {
  return {
    alias,
    type: 'parson',
    json: '{}',
  };
}

export function defaultSimsonSelection(alias: string): SimsonAsset {
  return {
    alias,
    type: SIMSON_TYPE_NAME,
    elm: '',
  };
}

export function defaultGenericElmAssetSelection(alias: string): GenericElmAsset {
  return {
    alias,
    type: GENERIC_ELM_ASSET_TYPE_NAME,
    elm: '',
  };
}

export function defaultLegacyJavascriptSelection(alias: string): LegacyJavascriptAsset {
  return {
    alias,
    type: 'operatorsearch',
    json: '{}',
  };
}

export type ElmFileDict = TypedDict<string>;

export interface UsedAsset {
  alias: string;
}

export interface ContentHashes {
  blockHashes: TypedDict<string>;
  quizMemoryHashes: TypedDict<string>;
}

export interface LessonWidgetSchema {
  [key: string]: any;
}

export interface LessonSourceObject {
  content: {
    camperdown: string;
    assets: LessonAsset[];
  };
}

export const BLANK_LESSON_SOURCE: LessonSourceObject = {
  content: {
    camperdown: '',
    assets: [],
  },
};

export interface LessonSummary {
  version_id: number;
  version_name: string;
  internal_change_reason: string;
  state: string;
  version_state: LessonState;
  created_ts: string;
  last_modified_ts: string;
  created_by_name: string;
  owned_by_name: string;
  owned_by_id: number;
  lesson_url: string;
  lesson_title: string;
  edit_url: string;
  lesson_source: Optional<LessonSourceObject>;
  // Not always included, maybe should move to a separate type
  parent_version_name?: string;
  latest_autosave_ts?: string;
  latest_autosave_by_name?: string;
  latest_autosave_by_id?: number;
  approx_num_users?: string;
  lesson_report_count?: number;
  likes?: number;
  dislikes?: number;
}

export interface CourseTitlesItem {
  title: string;
  versions_url: string;
}

export interface ExtendedLessonSummary extends LessonSummary {
  image: string;
  lesson_info_admin_url: string;
  lesson_info_id: number;
  courses: CourseTitlesItem[];
}

export interface LessonAutosave {
  autosave_id: number;
  lesson_source: LessonSourceObject;
  created_by: string;
  created_ts: string;
}

export interface LessonAutosaveSummary {
  autosave_id: number;
  created_by: string;
  created_ts: string;
}

export interface LessonHistoryItem {
  item_type: 'autosave' | 'version';
  version_name: string;
  owned_by: string;
  last_modified_ts: string;
  internal_change_reason: string;
  lesson_source: LessonSourceObject;
}

export const WARNING_GUTTER_MARKER_KEY = 'syntax-warning-gutter';
export const ELM_ERROR_MARK_CLASS_NAME = 'elm-error-highlight';

export type EditorView = 'camperdown' | 'assets' | 'settings' | 'compiledElmMain';

export const DEFAULT_EDITOR_VIEW: EditorView = 'camperdown';

export interface ParsedEditorHashPath {
  view: EditorView;
  subview?: string;
}

export interface SelectorGroup<T> {
  group_name: string;
  items: T[];
}

export type SelectorIndex = [number, number];

export interface DiagrammarExampleItem {
  title: string;
  description: string;
  diagram: {
    elm: string;
    javascript: string;
  };
}

export type DiagrammarExampleGroup = SelectorGroup<DiagrammarExampleItem>;

export type EditorStatusType = 'STATUS' | 'ERROR' | 'SUCCESS';
export type EditorStatusTuple = [string, EditorStatusType];

export interface TranspilerResponse {
  mainElm: string;
  additionalElm: ElmFileDict;
  additionalMains: string[];
  userLibs: string[];
  contentHashes: ContentHashes;
  lessonWidgetSchema: LessonWidgetSchema[];
  unifiedWarnings: TranspilerWarning[];
  interactivesManifest: InteractivesManifest;
  usedAssets: UsedAsset[];
}

export type SidebarTab = 'live_preview' | 'feedback' | 'snippets';

export interface OptionsLocalStorage {
  renderMathInLivePreview: boolean;
  isSidebarExpanded: boolean;
  currentSidebarTab: SidebarTab;
}

export interface CompiledElmObject {
  elm_main_module: string;
  additional_main_modules: string[];
  elm_files: ElmFileDict;
}

export type CompiledElmGetter = (route: Route) => CompiledElmObject;

export interface LessonLocalStorage {
  lessonSource: LessonSourceObject;
  localStorageTimestamp: number;
}

export type AssetCreationCategory = 'diagrams' | 'interactives';

// This should align with ReportType in brilliant/courses/lessonreports/types.py
export type LessonReportType =
  | 'TYPO'
  | 'VISUALS_INTERACTIVES_BROKEN'
  | 'DISPUTE'
  | 'TERM_HELP'
  | 'CLARIFICATION'
  | 'TOO_FAST'
  | 'TOO_SLOW';

export interface LessonReport {
  report_id: number;
  submitted_by: string;
  created_ts: string;
  user_id: number;
  profile_url: string;
  profile_url_part: string;
  is_staff: boolean;
  block: number;
  type: LessonReportType;
  contents: string;
}

export interface LessonFeedback {
  feedback_id: number;
  submitted_by: string;
  created_ts: string;
  user_id: number;
  profile_url: string;
  profile_url_part: string;
  is_staff: boolean;
  liked: boolean;
  contents: string;
}
